'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import samplesModel, { SAMPLES_MODE } from '../SamplesModel';
import './SamplesSelector.css';
import { Input } from '@/components/ui/input';
import {
  isAdminPage,
  isDebugPage,
} from '@/sharedTypes/helpers';
import { IconButton } from '@/components/ui/button';
import {
  PiMagnifyingGlass,
  PiPlusCircleLight,
  PiMusicNotesPlus,
  PiKeyLight,
  PiPasswordLight,
  PiUser,
  PiBookOpenText,
} from 'react-icons/pi';
import { jwtDecode } from 'jwt-decode';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import TooltipSamplesSelector from '@/components/Tooltips/TooltipSamplesSelector';
import TooltipFavoritesUnlogged from '@/components/Tooltips/TooltipFavoritesUnlogged';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { signIn, useSession } from 'next-auth/react';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import SamplesListView from './SamplesListView';
import AlbumsListView from '../Albums/AlbumsListView';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { apiSwagger } from '@/shared/api';
import { notifyStatus } from '@/sharedTypes/coreTypes';

type SelectorTab = 'samples' | 'albums' | 'playlists';

const tabToMode = (tab: SelectorTab) =>
  tab === 'albums' ? SAMPLES_MODE.ALBUMS :
  tab === 'playlists' ? SAMPLES_MODE.PLAYLISTS :
  SAMPLES_MODE.SELECT;

const modeToTab = (mode: typeof SAMPLES_MODE[keyof typeof SAMPLES_MODE]): SelectorTab =>
  mode === SAMPLES_MODE.ALBUMS ? 'albums' :
  mode === SAMPLES_MODE.PLAYLISTS ? 'playlists' :
  'samples';

const SamplesSelector = observer(() => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const activeTab = modeToTab(samplesModel.currentMode);
  const [areFavoritesShown, setAreFavoritesShown] = useState(false);
  const [albumPlusMenuOpen, setAlbumPlusMenuOpen] = useState(false);
  const [cpcDialogOpen, setCpcDialogOpen] = useState(false);
  const [cpcCode, setCpcCode] = useState('');
  const [cpcError, setCpcError] = useState('');
  const [cpcLoading, setCpcLoading] = useState(false);
  const [cpcConfirm, setCpcConfirm] = useState<{ albumId: string; albumType: string } | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [activeCreateType, setActiveCreateType] = useState<'internal' | 'official'>('internal');
  const [createAlbumType, setCreateAlbumType] = useState<'official' | 'admin_created'>('official');
  const coverInputRef = useRef<HTMLInputElement>(null);
  const albumPlusMenuRef = useRef<HTMLDivElement>(null);
  const albumTabMenuRef = useRef<HTMLDivElement>(null);
  const [albumTabMenuOpen, setAlbumTabMenuOpen] = useState(false);
  const { t } = useTranslation();
  const isUserLoggedIn = useSession().data?.accessToken;

  const isAdmin = (() => {
    if (!isUserLoggedIn) return false;
    try {
      const decoded = jwtDecode(isUserLoggedIn as string) as any;
      return decoded?.realm_access?.roles?.includes('darpiano-admin') || false;
    } catch { return false; }
  })();
  const toggleFavoritesView = () => {
    setAreFavoritesShown(!areFavoritesShown);
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('favoritesMenuToggled', { detail: { shown: !areFavoritesShown } }));
    }
  };

  useEffect(() => {
    triggerEvent('favoritesMenuToggled', { opened: areFavoritesShown });
  }, [areFavoritesShown]);

  // Close album plus menu on click outside
  useEffect(() => {
    if (!albumPlusMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (albumPlusMenuRef.current && !albumPlusMenuRef.current.contains(e.target as Node)) {
        setAlbumPlusMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [albumPlusMenuOpen]);

  // Close album tab menu on click outside
  useEffect(() => {
    if (!albumTabMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (albumTabMenuRef.current && !albumTabMenuRef.current.contains(e.target as Node)) {
        setAlbumTabMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [albumTabMenuOpen]);

  const handleCpcClaim = async () => {
    if (!cpcCode.trim()) return;
    setCpcLoading(true);
    setCpcError('');
    try {
      const res = await apiSwagger.albums.cpcClaimCreate({ code: cpcCode.trim() });
      const data = await res.json();
      if (!res.ok) {
        setCpcError(data.error || t('albums.cpc.invalid_code'));
        return;
      }
      // If entered from albums tab but the code is for a playlist, ask for confirmation
      if (activeTab === 'albums' && data.albumType === 'internal') {
        setCpcConfirm({ albumId: data.albumId, albumType: data.albumType });
        return;
      }
      setCpcDialogOpen(false);
      setCpcCode('');
      samplesModel.previousMode = samplesModel.currentMode;
      samplesModel.selectedAlbumId = data.albumId;
      samplesModel.currentMode = SAMPLES_MODE.ALBUM_DETAIL;
    } catch {
      setCpcError(t('albums.cpc.failed_claim'));
    } finally {
      setCpcLoading(false);
    }
  };

  const handleCpcConfirmAccept = () => {
    if (!cpcConfirm) return;
    setCpcDialogOpen(false);
    setCpcCode('');
    setCpcConfirm(null);
    samplesModel.previousMode = SAMPLES_MODE.PLAYLISTS;
    samplesModel.selectedAlbumId = cpcConfirm.albumId;
    samplesModel.currentMode = SAMPLES_MODE.ALBUM_DETAIL;
  };

  const handleCpcConfirmCancel = () => {
    setCpcConfirm(null);
  };

  const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    const reader = new FileReader();
    reader.onload = () => setCoverPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const resetCreateDialog = () => {
    setCreateTitle('');
    setCreateDesc('');
    setCoverFile(null);
    setCoverPreview(null);
    setCreateAlbumType('official');
  };

  const handleCreatePlaylist = async () => {
    if (!createTitle.trim()) return;
    setCreateLoading(true);
    // For admin album creation, use the selected type (official or admin_created)
    const resolvedType = activeCreateType === 'official' ? createAlbumType : activeCreateType;
    try {
      let coverImage: string | undefined;
      if (coverFile) {
        const formData = new FormData();
        formData.append('cover', coverFile);
        const uploadRes = await apiSwagger.albums.coverUploadCreate(formData as any);
        if (!uploadRes.ok) {
          window.popup?.(uploadRes.error?.error || t('albums.create.failed_cover_upload'), notifyStatus.failed);
          return;
        }
        coverImage = uploadRes.data?.coverUrl;
      }
      const res = await apiSwagger.albums.albumsCreate({
        title: createTitle.trim(),
        description: createDesc.trim() || undefined,
        albumType: resolvedType,
        coverImage,
      });
      const data = await res.json();
      if (!res.ok) {
        window.popup?.((data as any).error || t(resolvedType === 'internal' ? 'albums.create.failed_playlist' : 'albums.create.failed_album'), notifyStatus.failed);
        return;
      }
      setCreateDialogOpen(false);
      resetCreateDialog();
      samplesModel.previousMode = resolvedType === 'internal' ? SAMPLES_MODE.PLAYLISTS : SAMPLES_MODE.ALBUMS;
      samplesModel.selectedAlbumId = (data as any)._id;
      samplesModel.currentMode = SAMPLES_MODE.ALBUM_DETAIL;
    } catch (err) {
      window.popup?.(t(resolvedType === 'internal' ? 'albums.create.failed_playlist' : 'albums.create.failed_album'), notifyStatus.failed);
    } finally {
      setCreateLoading(false);
    }
  };

  // Close menus when switching tabs
  const handleTabChange = useCallback((tab: SelectorTab) => {
    samplesModel.currentMode = tabToMode(tab);
    setAlbumPlusMenuOpen(false);
    setAlbumTabMenuOpen(false);
  }, []);

  // Remove focus on mobile devices when component mounts
  useEffect(() => {
    const isMobileDevice =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    const isTouchDevice =
      'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isMobile = isMobileDevice || isTouchDevice;

    if (isMobile && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.blur();
      }, 0);
    }
  }, []);

  return (
    <div className='flex flex-col rounded-md max-h-full overflow-hidden'>
      <div className='border-b bg-white'>
        <div className='flex items-center gap-6 py-2 px-2 md:py-3'>
          <div className='relative w-full'>
            <div className='absolute text-zinc-500 w-8 top-0 bottom-0 flex items-center justify-center'>
              <PiMagnifyingGlass size={16} />
            </div>
            <Input
              ref={inputRef}
              className='pl-8'
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              data-testid='samples_search_input'
              placeholder={t('samples.search')}
            />
          </div>
          <div className='flex items-center gap-1'>
            <TooltipFavoritesUnlogged
              toggleFavoritesView={toggleFavoritesView}
              areFavoritesShown={areFavoritesShown}
              isUserLoggedIn={isUserLoggedIn}
            />
            {!(isAdminPage() || isDebugPage()) && (
              <div className='hidden md:block [@media(hover:hover)]:block [@media(pointer:fine)]:block'>
                <TooltipSamplesSelector />
              </div>
            )}
            {(isAdminPage() || isDebugPage()) && activeTab === 'samples' && (
              <IconButton
                data-testid='sample_upload_btn'
                onClick={() => {
                  samplesModel.currentMode = SAMPLES_MODE.UPLOAD;
                }}
              >
                <PiPlusCircleLight size={28} />
              </IconButton>
            )}
            {activeTab === 'albums' && (
              <div className='relative' ref={albumTabMenuRef}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      data-testid='album_plus_btn'
                      onClick={() => {
                        if (!isUserLoggedIn) { signIn('keycloak'); return; }
                        if (isAdmin) { setAlbumTabMenuOpen((prev) => !prev); }
                        else { setCpcDialogOpen(true); }
                      }}
                    >
                      <PiPlusCircleLight size={28} />
                    </IconButton>
                  </TooltipTrigger>
                  {!isUserLoggedIn && (
                    <TooltipContent className='w-58' sideOffset={8} side='right'>
                      <div className='w-full m-2'>
                        <p className='text-sm'>
                          <span className='inline-flex align-middle mr-1'>
                            <PiUser size={20} className='fill-accent-blue' />
                          </span>
                          {t('tooltip.albums.notLoggedIn')}
                        </p>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
                {albumTabMenuOpen && (
                  <div className='absolute right-0 top-full mt-1 z-50 bg-white border rounded-lg shadow-lg py-1 w-48'>
                    <button
                      data-testid='album_create_btn'
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-100 transition-colors'
                      onClick={() => {
                        setAlbumTabMenuOpen(false);
                        setActiveCreateType('official');
                        setCreateDialogOpen(true);
                      }}
                    >
                      <PiBookOpenText size={18} />
                      {t('albums.create_album')}
                    </button>
                    <button
                      data-testid='album_enter_code_btn'
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-100 transition-colors'
                      onClick={() => {
                        setAlbumTabMenuOpen(false);
                        setCpcDialogOpen(true);
                      }}
                    >
                      <PiKeyLight size={18} />
                      {t('albums.enter_by_code')}
                    </button>
                  </div>
                )}
              </div>
            )}
            {activeTab === 'playlists' && (
              <div className='relative' ref={albumPlusMenuRef}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconButton
                      data-testid='playlist_plus_btn'
                      onClick={() => isUserLoggedIn ? setAlbumPlusMenuOpen((prev) => !prev) : signIn('keycloak')}
                    >
                      <PiPlusCircleLight size={28} />
                    </IconButton>
                  </TooltipTrigger>
                  {!isUserLoggedIn && (
                    <TooltipContent className='w-58' sideOffset={8} side='right'>
                      <div className='w-full m-2'>
                        <p className='text-sm'>
                          <span className='inline-flex align-middle mr-1'>
                            <PiUser size={20} className='fill-accent-blue' />
                          </span>
                          {t('tooltip.playlists.notLoggedIn')}
                        </p>
                      </div>
                    </TooltipContent>
                  )}
                </Tooltip>
                {albumPlusMenuOpen && (
                  <div className='absolute right-0 top-full mt-1 z-50 bg-white border rounded-lg shadow-lg py-1 w-48'>
                    <button
                      data-testid='playlist_create_btn'
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-100 transition-colors'
                      onClick={() => {
                        setAlbumPlusMenuOpen(false);
                        setActiveCreateType('internal');
                        setCreateDialogOpen(true);
                      }}
                    >
                      <PiMusicNotesPlus size={18} />
                      {t('albums.create_playlist')}
                    </button>
                    <button
                      data-testid='cpc_enter_code_btn'
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-zinc-100 transition-colors'
                      onClick={() => {
                        setAlbumPlusMenuOpen(false);
                        setCpcDialogOpen(true);
                      }}
                    >
                      <PiKeyLight size={18} />
                      {t('albums.enter_by_code')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='flex border-b'>
        {(['samples', 'albums', 'playlists'] as SelectorTab[]).map((tab) => (
          <button
            key={tab}
            data-testid={`${tab}-tab`}
            className={cn(
              'flex-1 py-2 text-sm font-medium transition-colors',
              activeTab === tab
                ? 'border-b-2 border-accent-blue text-accent-blue'
                : 'text-zinc-500 hover:text-zinc-700'
            )}
            onClick={() => handleTabChange(tab)}
          >
            {t(`tabs.${tab}`)}
          </button>
        ))}
      </div>
      <div className='overflow-auto flex-1 min-h-1/2'>
        {activeTab === 'samples' && (
          <SamplesListView searchQuery={searchQuery} areFavoritesShown={areFavoritesShown} />
        )}
        {activeTab === 'albums' && (
          <AlbumsListView
            areFavoritesShown={areFavoritesShown}
            albumTypes={['official', 'admin_created']}
            emptyMessage={t('albums.no_albums')}
            emptyFavMessage={t('albums.no_albums_favorites')}
          />
        )}
        {activeTab === 'playlists' && (
          <AlbumsListView
            areFavoritesShown={areFavoritesShown}
            albumTypes={['internal']}
            emptyMessage={t('albums.no_playlists')}
            emptyFavMessage={t('albums.no_playlists_favorites')}
          />
        )}
      </div>
      <Dialog open={cpcDialogOpen} onOpenChange={(open) => {
        setCpcDialogOpen(open);
        if (!open) { setCpcCode(''); setCpcError(''); setCpcConfirm(null); }
      }}>
        <DialogContent className='sm:max-w-md'>
          {cpcConfirm ? (
            <>
              <DialogHeader className='items-center'>
                <DialogTitle>{t('albums.cpc.is_playlist_title')}</DialogTitle>
                <DialogDescription>
                  {t('albums.cpc.is_playlist_description')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <button
                  data-testid='cpc-confirm-back-btn'
                  className='px-4 py-2 text-sm rounded-md border border-zinc-300 hover:bg-zinc-50 transition-colors'
                  onClick={handleCpcConfirmCancel}
                >
                  {t('albums.cpc.back')}
                </button>
                <button
                  data-testid='cpc-confirm-add-btn'
                  className='px-4 py-2 text-sm rounded-md text-white bg-accent-blue hover:bg-accent-blue/90 transition-colors'
                  onClick={handleCpcConfirmAccept}
                >
                  {t('albums.cpc.add_playlist')}
                </button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader className='items-center'>
                <div className='w-14 h-14 rounded-full bg-zinc-100 flex items-center justify-center mb-2'>
                  <PiKeyLight size={28} className='text-zinc-600' />
                </div>
                <DialogTitle>{t('albums.cpc.enter_code_title')}</DialogTitle>
                <DialogDescription>
                  {t('albums.cpc.enter_code_description')}
                </DialogDescription>
              </DialogHeader>
              <Input
                value={cpcCode}
                onChange={(e) => { setCpcCode(e.target.value); setCpcError(''); }}
                placeholder='XXXXX-XXXXX'
                data-testid='cpc_code_input'
                onKeyDown={(e) => { if (e.key === 'Enter') handleCpcClaim(); }}
              />
              {cpcError && (
                <p className='text-sm text-red-500'>{cpcError}</p>
              )}
              <DialogFooter>
                <button
                  className='px-4 py-2 text-sm rounded-md border border-zinc-300 hover:bg-zinc-50 transition-colors'
                  onClick={() => setCpcDialogOpen(false)}
                >
                  {t('albums.cpc.cancel')}
                </button>
                <button
                  data-testid='cpc_submit_btn'
                  className={cn(
                    'px-4 py-2 text-sm rounded-md text-white transition-colors',
                    cpcLoading || !cpcCode.trim()
                      ? 'bg-accent-blue/50 cursor-not-allowed'
                      : 'bg-accent-blue hover:bg-accent-blue/90'
                  )}
                  onClick={handleCpcClaim}
                  disabled={cpcLoading || !cpcCode.trim()}
                >
                  {cpcLoading ? t('albums.cpc.adding') : t('albums.cpc.add')}
                </button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={createDialogOpen} onOpenChange={(open) => {
        setCreateDialogOpen(open);
        if (!open) resetCreateDialog();
      }}>
        <DialogContent data-testid='create-playlist-dialog' className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{activeCreateType === 'official' ? t('albums.create.new_album_title') : t('albums.create.new_playlist_title')}</DialogTitle>
            <DialogDescription>
              {activeCreateType === 'official'
                ? t('albums.create.album_description')
                : t('albums.create.playlist_description')}
            </DialogDescription>
          </DialogHeader>
          <div className='flex flex-col items-center gap-1'>
            <input
              ref={coverInputRef}
              type='file'
              accept='image/png,image/jpeg,image/webp'
              className='hidden'
              onChange={handleCoverSelect}
            />
            <button
              type='button'
              data-testid='create-playlist-cover-zone'
              className='w-[128px] h-[160px] rounded-lg border-2 border-dashed border-zinc-300 hover:border-zinc-400 transition-colors flex flex-col items-center justify-center gap-2 overflow-hidden'
              onClick={() => coverInputRef.current?.click()}
            >
              {coverPreview ? (
                <img src={coverPreview} alt='Cover preview' data-testid='create-playlist-cover-preview' className='w-full h-full object-cover' />
              ) : (
                <>
                  <PiPlusCircleLight size={28} className='text-zinc-400' />
                  <span className='text-xs text-zinc-400'>{t('albums.create.add_cover')}</span>
                </>
              )}
            </button>
            {coverPreview && (
              <button
                type='button'
                className='text-xs text-red-500 hover:text-red-600 transition-colors'
                onClick={() => { setCoverFile(null); setCoverPreview(null); }}
              >
                {t('albums.create.remove_cover')}
              </button>
            )}
          </div>
          <div className='flex flex-col gap-3'>
            {activeCreateType === 'official' && (
              <div className='flex flex-col gap-1.5'>
                <Label>{t('albums.create.type_label')}</Label>
                <div className='flex rounded-md border border-input overflow-hidden text-sm'>
                  <button
                    type='button'
                    className={cn('flex-1 py-1.5 transition-colors', createAlbumType === 'official' ? 'bg-accent-blue text-white' : 'hover:bg-zinc-50')}
                    onClick={() => setCreateAlbumType('official')}
                  >
                    {t('albums.create.type_official')}
                  </button>
                  <button
                    type='button'
                    className={cn('flex-1 py-1.5 transition-colors border-l', createAlbumType === 'admin_created' ? 'bg-accent-blue text-white' : 'hover:bg-zinc-50')}
                    onClick={() => setCreateAlbumType('admin_created')}
                  >
                    {t('albums.create.type_admin_created')}
                  </button>
                </div>
              </div>
            )}
            <div className='flex flex-col gap-1.5'>
              <Label>{activeCreateType === 'official' ? t('albums.create.album_title_label') : t('albums.create.playlist_title_label')}</Label>
              <Input
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                placeholder={activeCreateType === 'official' ? t('albums.create.album_title_placeholder') : t('albums.create.playlist_title_placeholder')}
                data-testid='create_playlist_title'
              />
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label>{t('albums.create.description_label')}</Label>
              <textarea
                className='flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none'
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                placeholder={activeCreateType === 'official' ? t('albums.create.album_desc_placeholder') : t('albums.create.playlist_desc_placeholder')}
                data-testid='create_playlist_desc'
              />
            </div>
          </div>
          <DialogFooter>
            <button
              className='px-4 py-2 text-sm rounded-md border border-zinc-300 hover:bg-zinc-50 transition-colors'
              onClick={() => setCreateDialogOpen(false)}
            >
              {t('albums.create.cancel')}
            </button>
            <button
              data-testid='create-playlist-submit-btn'
              className={cn(
                'px-4 py-2 text-sm rounded-md text-white transition-colors',
                createLoading || !createTitle.trim()
                  ? 'bg-accent-blue/50 cursor-not-allowed'
                  : 'bg-accent-blue hover:bg-accent-blue/90'
              )}
              onClick={handleCreatePlaylist}
              disabled={createLoading || !createTitle.trim()}
            >
              {createLoading ? t('albums.create.creating') : t('albums.create.create')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
});

export default SamplesSelector;
